<!-- eslint-disable vue/no-useless-template-attributes -->

<template>
  <div
    ref="scrollerEl"
    :class="[
      `flex grow flex-col overflow-y-auto`,
      // lg because that's the breakpoint at which we start showing column headers.
      hasGrouping || isHeaderShown ? `pb-4` : `pb-4 pt-2 lg:pt-4`,
    ]"
    @scroll="updateScrollTop"
  >
    <table ref="tableEl" class="mb-auto w-full">
      <thead>
        <tr>
          <Draggable v-model="headers" axis="horizontal">
            <TableColumnHeader
              v-for="(header, index) in headers"
              ref="ths"
              :key="header.id"
              :header="header"
              :reorderable="isReorderable"
              :index="index"
              :shown="isHeaderShown"
            />
          </Draggable>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td class="p-0" :style="{ height: `${scrollOffset}px` }"></td>
        </tr>

        <template v-for="{ row, index } in visibleRows" :key="row.id">
          <TableRowGroup
            v-if="row.getIsGrouped()"
            :row="row"
            :index="index"
            @measure="measureElement"
          />
          <TableRow
            v-else
            :table="table"
            :row="row"
            :index="index"
            @measure="measureElement"
          >
            <template v-if="slots.dragPreview" #dragPreview="{ item }">
              <slot name="dragPreview" :item="item" />
            </template>
          </TableRow>
        </template>

        <tr>
          <td
            class="p-0"
            :style="{
              height: `${totalHeight - scrollOffset - visibleHeight}px`,
            }"
          ></td>
        </tr>
      </tbody>
    </table>

    <slot name="footer"></slot>
  </div>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import {
  computed,
  isReadonly,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRef,
  useTemplateRef,
  watch,
} from "vue";

import { mapDefined } from "@/lib/collections";
import { useMediaQuery } from "@/lib/composables/media-query";
import Draggable from "@/lib/pragmatic-dnd/Draggable.vue";
import {
  type InternalTableInstance,
  isFixedColumn,
} from "@/lib/tanstack-table";
import type { GenericModel } from "@/types/model";

import TableColumnHeader from "./TableColumnHeader.vue";
import TableRow from "./TableRow.vue";
import TableRowGroup from "./TableRowGroup.vue";

const props = defineProps<{
  table: InternalTableInstance<Model>;
  hideHeader?: boolean;
}>();

const slots = defineSlots<{
  footer(): unknown;
  dragPreview?(props: { item: Model }): unknown;
}>();

const table = props.table;
const instance = table.instance;

const hasGrouping = computed(() => !!instance.getState().grouping?.length);

const isReorderable = ((): boolean => {
  const visibility = table.columnVisibility;
  return !!visibility && !isReadonly(visibility);
})();

const headers = computed({
  get() {
    const groups = instance.getHeaderGroups();
    if (groups.length === 0) {
      return [];
    }

    return groups[0].headers;
  },
  set(next) {
    if (!isReorderable) {
      return;
    }

    const customIds = mapDefined(next, (header) => {
      const column = header.column;
      if (!isFixedColumn(column)) {
        return column.id;
      }
    });

    table.columnVisibility!.value = {
      columns: customIds,
      reordered: true,
    };
  },
});
const rows = computed(() => {
  return instance.getRowModel().rows.flatMap((row) => {
    return [row, ...row.getLeafRows()];
  });
});

const isMobile = useMediaQuery(`(max-width: 1023px)`);
const isHeaderShown = computed(() => {
  return !props.hideHeader && !isMobile.value;
});

const paddingStart = computed(() => {
  return isHeaderShown.value ? 40 : !hasGrouping.value ? 16 : 0;
});

// #region Virtual table
const BUFFER = 20;
const ESTIMATE_HEIGHT = 26;

const scroller = useTemplateRef("scrollerEl");

const scrollTop = shallowRef(0);
const scrollWindowHeight = shallowRef<number>();
const adjustedScrollTop = toRef(() => scrollTop.value + paddingStart.value);

const elemCache = new Map<number, HTMLElement>();
const heightCache = ref<number[]>([]);
watch(
  () => rows.value.length,
  (length) => {
    const curr = heightCache.value;
    const currLength = curr.length;

    if (length > currLength) {
      heightCache.value = curr.concat(
        new Array(length - currLength).fill(ESTIMATE_HEIGHT)
      );
    } else if (length < currLength) {
      heightCache.value = curr.slice(0, length);
    }
  },
  { immediate: true }
);

const cumulativeHeights = computed(() => {
  const $heightCache = heightCache.value;

  const cumulative: number[] = [];
  let total = 0;

  for (let idx = 0, len = $heightCache.length; idx < len; idx++) {
    const height = $heightCache[idx];
    cumulative.push((total += height));
  }

  return cumulative;
});

interface VisibleRange {
  start: number;
  end: number;
}

const visibleRange = computed((prev?: VisibleRange): VisibleRange => {
  const $scrollWindowHeight = scrollWindowHeight.value;
  const $heights = cumulativeHeights.value;

  if ($scrollWindowHeight === undefined) {
    // We don't really know how much to display, let's just assume it's as tall
    // as the screen height.
    const estimate = Math.floor(window.innerHeight / ESTIMATE_HEIGHT);

    return { start: 0, end: Math.min($heights.length, estimate + BUFFER) };
  }

  const $scrollTop = adjustedScrollTop.value;

  const start = binarySearch($heights, $scrollTop, 0);
  const end = binarySearch($heights, $scrollTop + $scrollWindowHeight, start);

  const range: VisibleRange = {
    start: Math.max(0, start - BUFFER),
    end: Math.min($heights.length, end + 1 + BUFFER),
  };

  // Reuse the previous range object if it's the same, don't have
  // to recompute downstream.
  if (prev && prev.start === range.start && prev.end === range.end) {
    return prev;
  }

  return range;
});

const visibleRows = computed(() => {
  const { start, end } = visibleRange.value;

  return rows.value.slice(start, end).map((row, index) => {
    return { row: row, index: index + start };
  });
});

const totalHeight = computed(() => {
  const $heights = cumulativeHeights.value;
  return $heights.length > 0 ? $heights[$heights.length - 1] : 0;
});

const scrollOffset = computed(() => {
  const $start = visibleRange.value.start;
  return $start === 0 ? 0 : cumulativeHeights.value[$start - 1];
});

const visibleHeight = computed(() => {
  const { start, end } = visibleRange.value;
  const $heights = cumulativeHeights.value;

  if ($heights.length === 0 || start >= $heights.length) {
    return 0;
  }

  const startHeight = start > 0 ? $heights[start - 1] : 0;
  const endHeight = $heights[end - 1] ?? 0;

  return endHeight - startHeight;
});

const rowRO = new ResizeObserver((entries) => {
  const $heightCache = heightCache.value;

  for (let idx = 0, len = entries.length; idx < len; idx++) {
    const entry = entries[idx];

    const target = entry.target as HTMLElement;
    if (!target.isConnected) {
      // Stale observer entries, these will report as 0px rect height.
      rowRO.unobserve(target);
      continue;
    }

    const index = indexFromElement(target);
    if (index === null) {
      continue;
    }

    const height = entry.contentRect.height;
    $heightCache[index] = height;
  }
});

const updateScrollTop = () => {
  scrollTop.value = scroller.value!.scrollTop;
};

let scrollWindowWidth = 0;
const updateContainerHeight = (entries: ResizeObserverEntry[]) => {
  const entry = entries[0];
  const rect = entry.contentRect;

  scrollWindowHeight.value = rect.height;

  if (scrollWindowWidth !== rect.width) {
    scrollWindowWidth = rect.width;
    updateColumnSizing();
  }
};

const measureElement = (node: any) => {
  if (!node) {
    elemCache.forEach((cachedElem, key) => {
      if (!cachedElem.isConnected) {
        rowRO.unobserve(cachedElem);
        elemCache.delete(key);
      }
    });

    return;
  }

  const index = indexFromElement(node);
  if (index === null) {
    return;
  }

  const prevNode = elemCache.get(index);
  if (node !== prevNode) {
    if (prevNode) {
      rowRO.unobserve(prevNode);
    }

    rowRO.observe(node);
    elemCache.set(index, node);
  }
};

onMounted(() => {
  const scrollerRO = new ResizeObserver(updateContainerHeight);
  scrollerRO.observe(scroller.value!);

  onUnmounted(() => {
    scrollerRO.disconnect();
    rowRO.disconnect();
  });
});

const indexFromElement = (node: HTMLElement) => {
  const rawIndex = node.dataset.index as string | undefined;
  if (rawIndex === undefined) {
    return null;
  }

  return parseInt(rawIndex, 10);
};

const binarySearch = (arr: number[], target: number, low: number): number => {
  let high = arr.length - 1;
  let ans = arr.length; // default to arr.length if target is greater than all elements

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (arr[mid] >= target) {
      ans = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return ans;
};

// #endregion

// #region Messy column resizing
// This is used to keep columns at a consistent width, rather than fluctuating
// depending on what rows are being rendered by the virtual list.

const tableEl = useTemplateRef("tableEl");
const headerEls = useTemplateRef("ths");

const updateColumnSizing = () => {
  const $tableEl = tableEl.value;
  const $headerEls = headerEls.value;

  if (!$tableEl || !$headerEls) {
    return;
  }

  // Put the table layout in auto, and unset the widths we set on the cells.
  $tableEl.style.tableLayout = "auto";

  for (const header of $headerEls) {
    const cell = header?.element;
    if (!cell) {
      continue;
    }

    cell.style.width = "";
  }

  // Now, set width on all the cells based on the browser's layout.
  for (const header of $headerEls) {
    const cell = header?.element;
    if (!cell) {
      continue;
    }

    cell.style.width = `${cell.getBoundingClientRect().width}px`;
  }

  // Finally, set the table layout back to fixed.
  $tableEl.style.tableLayout = "fixed";
};

watch(
  [
    () => instance.getCoreRowModel().rows,
    () => instance.getVisibleFlatColumns(),
  ],
  updateColumnSizing,
  // Only call *after* Vue has updated the DOM.
  { flush: "post" }
);

// #endregion
</script>
